import { ref, computed } from 'vue'
import eventbus from '@/eventbus.js'
import settings from '@/lib/settings'
import router from '@/router.js' // to show login (:-/)

// Okay, this kind of sucked so it's been largely refactored. We're trying to keep 
// the store out of things, and let it be a dumb client.
//
// We have a login screen, which gathers credentials. It then dispatches a store action
// called `fetchToken` to get authentication details as a JWT. The store commits these by
// setting currentAuthRef.value, which we handle in our setter by persisting in `settings`
// 
// We then emit didReceiveToken
//
// Then, everything `import`s `isLoggedIn` which references the `currentAuthRef`.
// When loading, we can `await authenticationComplete()` which will return a promise.
// -> If we're logged in, the promise resolves immediately.
// -> If not, the router is pushed to the login page and the promise hangs until
//    login is complete.
//

const timeoutHack = ref(0);
let   timeoutHackHandle;
let 	_authenticationPromise = void 0;
let 	_authenticationDidComplete = void 0;

function registerAuthTimeoutIfNeeded(auth) {
	// Reevaluate at moment of timeout
	const timeoutAt = auth.exp * 1000 ?? Date.now() + 1000;
	const timeoutIn = timeoutAt - Date.now();
	
	console.debug(`registerAuthTimeout: will timeout at ${timeoutAt} (in ${timeoutIn})`);
	console.debug(`registerAuthTimeout: current timeoutHack value ${timeoutHack.value}`);
	
	timeoutHackHandle = setTimeout(() => {
		console.debug('registerAuthTimeout: will timeout now', timeoutHack.value);
		timeoutHackHandle = null;
		timeoutHack.value = auth.exp; // make sure this is changing
	}, timeoutIn);
	
}

// Things will await when they load.
export function authenticationComplete() {
	if (isLoggedIn())
		return new Promise(resolve => resolve());

	// if we have a promise already, allow them to wait for it
	if (_authenticationPromise)
		return _authenticationPromise;
	
	// We're going to make a promise to wait on
	console.debug('await authenticationComplete');

	// We need to show a login form here.
	redirectToLogin()

	_authenticationPromise = new Promise((resolve, reject) => {
		_authenticationDidComplete = resolve;
	});
	
	return _authenticationPromise;
}


// Call this to show the login screen

export function redirectToLogin() {
	const currentRoute = router.currentRoute?.value;

	if (currentRoute.name !== 'login') {
		router.push({ name: 'login', query: {t: currentRoute?.fullPath} }) 
	}
}

export function handleAuthenticationFailure() {
	currentAuthRef.value = null;
	redirectToLogin();
}

// This returns an `auth` object computed
// from the persisted token.
//
export let currentAuthRef = computed({
  get: () => {
    let auth,
        tok = settings.token;

    if (!tok) {
      console.warn("No token");
      return null;
    }
  
    try {
      auth = decodeToken(tok);
      console.debug("auth: decode token", auth);
    }
    catch (e) { 
      console.error("Could not decode auth token", e, tok);
    }
    console.debug('currentAuth', auth);
    return auth;
  },
  set: newValue => {
    console.debug("Auth: setting currentAuth", newValue);
    if (newValue === undefined || newValue === null) {
			console.warn("Auth: cleared");
	    settings.token = null;
    }
		else {
			console.warn("Auth: persisting");

			const auth = decodeToken(newValue);
			if (auth) {
				settings.token = newValue;
				registerAuthTimeoutIfNeeded(auth);
				if (_authenticationDidComplete) {
				console.log("set auth: fire", _authenticationDidComplete)
					_authenticationDidComplete();
					_authenticationPromise = null;
					_authenticationDidComplete = null;
				}
				eventbus.emit('loginDidSucceed', auth);
			}
		}
  }
});

export let isLoggedInRef = computed(() => {
	const h = timeoutHack.value;
  const auth = currentAuthRef.value

  console.debug("isLoggedInRef: currentAuth sub", auth?.sub)
  console.debug(`isLoggedInRef: tokenExpiresAt: ${auth?.exp} (in ${(auth?.exp * 1000) > Date.now() ? 'future' : 'past'})`, )
  console.debug(`isLoggedInRef: timeoutHack   : ${h}`, )

  const loggedIn = undefined !== auth?.sub &&
         (auth?.exp * 1000) > Date.now() &&
				 (h ?? 0) > -1;
				 
  if (loggedIn) {
		registerAuthTimeoutIfNeeded(auth);
		return true
	}
	
	return false;
});

export function decodeToken(tok) {
  if (!tok?.split) return {};
  const base64Url = tok.split('.')[1]; //console.debug('base64Url:', base64Url);
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/'); //console.debug('base64:', base64);
  const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
  
  return JSON.parse(jsonPayload);
}

export function currentAuth() {
  return currentAuth.value;
}

export function isLoggedIn() {
  const f = !!isLoggedInRef.value;
  return f;
}
